import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {ReitHolding, ReitDividendPayment} from "@/@types/reit.models.ts";

export type TimePeriod = '3M' | '6M' | '1Y' | 'ALL';

export interface TrendDataPoint {
  date: string;
  value: number;
}

export interface REITValueTrendChartProps {
  holding: ReitHolding;
  payments: ReitDividendPayment[];
  period: TimePeriod;
}

export function filterByPeriod(items: TrendDataPoint[], period: TimePeriod): TrendDataPoint[] {
  if (period === 'ALL') return items;
  const now = new Date();
  const months = period === '3M' ? 3 : period === '6M' ? 6 : 12;
  const cutoff = new Date(now.getFullYear(), now.getMonth() - months, now.getDate());
  return items.filter((item) => new Date(item.date) >= cutoff);
}

function buildTrendData(holding: ReitHolding, payments: ReitDividendPayment[]): TrendDataPoint[] {
  const value = holding.units_held * holding.current_price_per_unit;
  return payments.map((p) => ({ date: p.payment_date, value }));
}

function formatCurrency(value: number, currency: string): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value);
}

function CustomTooltip({ active, payload, currency }: any) {
  if (!active || !payload?.length) return null;
  const { date, value } = payload[0].payload as TrendDataPoint;
  const formatted = new Date(date).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
  return (
    <div className="bg-background border border-border rounded-lg shadow-lg p-3">
      <p className="text-sm text-muted-foreground mb-1">{formatted}</p>
      <p className="text-base font-semibold">{formatCurrency(value, currency)}</p>
    </div>
  );
}

export function REITValueTrendChart({ holding, payments, period }: REITValueTrendChartProps) {
  const allPoints = buildTrendData(holding, payments);
  const filtered = filterByPeriod(allPoints, period);

  return (
    <Card>
      <CardHeader>
        <CardTitle>Value Trend</CardTitle>
      </CardHeader>
      <CardContent>
        {filtered.length === 0 ? (
          <div
            className="flex items-center justify-center h-[300px] text-muted-foreground text-sm text-center px-4"
            data-testid="empty-state"
          >
            No trend data available. Record dividend payments to see the value trend.
          </div>
        ) : (
          <div
            className="w-full overflow-x-auto"
            role="img"
            aria-label={`Value trend chart for ${holding.reit_name} showing estimated market value over time`}
          >
            <div className="min-w-[500px]">
              <ResponsiveContainer width="100%" height={300}>
                <LineChart
                  data={filtered}
                  margin={{ top: 5, right: 20, left: 10, bottom: 5 }}
                  aria-label={`Value trend chart for ${holding.reit_name}`}
                >
                  <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                  <XAxis
                    dataKey="date"
                    tickFormatter={(v) =>
                      new Date(v).toLocaleDateString('en-US', { month: 'short', year: 'numeric' })
                    }
                    className="text-xs"
                  />
                  <YAxis
                    tickFormatter={(v) => formatCurrency(v, holding.currency)}
                    className="text-xs"
                    width={90}
                  />
                  <Tooltip content={<CustomTooltip currency={holding.currency} />} />
                  <Line
                    type="monotone"
                    dataKey="value"
                    stroke="hsl(var(--primary))"
                    strokeWidth={2}
                    dot={{ fill: 'hsl(var(--primary))', r: 4 }}
                    activeDot={{ r: 6 }}
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
